import z from 'zod'
import env from '@/config/env'
import { type CodeResponse, GoogleOAuthProvider, googleLogout } from '@react-oauth/google'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { decodeJwt } from 'jose'
import { trpc } from '@/config/trpc'
import {
  AUTH_LOGOUT_EVENT,
  SESSION_TOKEN_KEY,
  clearTokens,
  getRefreshToken,
  getSessionToken,
  refreshTokens,
  sessionTokenIsExpiringSoon,
  setTokens,
} from '@/config/auth'
import { usePostHog } from 'posthog-js/react'

const UserLoginContext = createContext<{
  user: User | null
  isPending: boolean
  firstLogin: boolean
  onCodeResponse: (codeResponse: CodeResponse, redirectUri: string) => Promise<void>
  onAuthSuccess: (sessionToken: string, refreshToken: string, firstLogin?: boolean) => void
  updateSessionToken: (sessionToken: string) => void
  logout: () => void
} | null>(null)

export const UserLoginProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <GoogleOAuthProvider clientId={env.VITE_GOOGLE_CLIENT_ID!}>
      <Child>{children}</Child>
    </GoogleOAuthProvider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useUserContext = () => {
  const context = useContext(UserLoginContext)
  if (!context) throw new Error('useUserContext must be used within UserLoginProvider')
  return context
}

const zPayload = z.object({
  id: z.number(),
  email: z.string(),
  isAdmin: z.boolean(),
  provider: z.enum(['google', 'userpass']),
  exp: z.number(),
  iat: z.number(),
})

type User = {
  id: number
  email: string
  isAdmin: boolean
  provider: 'google' | 'userpass'
}

const userFromPayload = (payload: z.infer<typeof zPayload>): User => ({
  id: payload.id,
  email: payload.email,
  isAdmin: payload.isAdmin,
  provider: payload.provider,
})

const Child = ({ children }: { children: React.ReactNode }) => {
  const posthog = usePostHog()

  const [user, setUser] = useState<User | null>(null)
  const [isPending, setIsPending] = useState(true)
  const [firstLogin, setFirstLogin] = useState(false)

  const { mutateAsync: authorize } = trpc.auth.authorize.useMutation()
  const { mutateAsync: signOut } = trpc.auth.signOut.useMutation()

  const logoutRef = useRef(() => {})

  // Bootstrap: load user from existing session token, refreshing if needed.
  useEffect(() => {
    const bootstrap = async () => {
      const sessionToken = getSessionToken()
      const refreshToken = getRefreshToken()

      if (!sessionToken && !refreshToken) return

      try {
        let activeToken = sessionToken
        if (!activeToken || sessionTokenIsExpiringSoon(activeToken)) {
          const result = await refreshTokens()
          activeToken = result.sessionToken
        }
        const decoded = zPayload.parse(decodeJwt(activeToken))
        const u = userFromPayload(decoded)
        setUser(u)
        posthog.identify(u.email, { ...u })
      } catch (e) {
        console.error('Failed to bootstrap session:', e)
        logoutRef.current()
      }
    }

    bootstrap().finally(() => setIsPending(false))
  }, [posthog])

  // Listen for forced logout from interceptor / other tabs.
  useEffect(() => {
    const onLogout = () => logoutRef.current()
    window.addEventListener(AUTH_LOGOUT_EVENT, onLogout)
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, onLogout)
  }, [])

  // handlers

  const onCodeResponse = async (codeResponse: CodeResponse, redirectUri: string) => {
    posthog.capture('auth_login_attempted', { provider: 'google' })
    try {
      const result = await authorize({
        provider: 'google',
        code: codeResponse.code,
        redirectUri,
      })

      if (!result.ok) {
        // Google auth never returns USER_NOT_VERIFIED today, but track defensively if it does.
        posthog.capture('auth_login_failed', { provider: 'google', reason: 'user_not_verified' })
        return
      }
      onAuthSuccess(result.sessionToken, result.refreshToken, result.firstLogin)
    } catch (error) {
      posthog.capture('auth_login_failed', {
        provider: 'google',
        reason: 'unknown',
        message: error instanceof Error ? error.message : undefined,
      })
      throw error
    }
  }

  const onAuthSuccess = (sessionToken: string, refreshToken: string, isFirstLogin?: boolean) => {
    const decoded = zPayload.parse(decodeJwt(sessionToken))
    const u = userFromPayload(decoded)
    setUser(u)
    setFirstLogin(isFirstLogin ?? false)
    posthog.identify(u.email, { ...u })
    posthog.capture('auth_login_succeeded', {
      provider: u.provider,
      first_login: isFirstLogin ?? false,
    })
    setTokens(sessionToken, refreshToken)
  }

  const updateSessionToken = (sessionToken: string) => {
    const decoded = zPayload.parse(decodeJwt(sessionToken))
    setUser(userFromPayload(decoded))
    localStorage.setItem(SESSION_TOKEN_KEY, sessionToken)
  }

  const logout = () => {
    const refreshToken = getRefreshToken()
    if (refreshToken) {
      // Best-effort revocation; don't block UI on it.
      signOut({ refreshToken }).catch((e) => console.warn('signOut failed:', e))
    }
    posthog.capture('auth_logout')
    clearTokens()
    setUser(null)
    googleLogout()
    posthog.reset()
  }
  useEffect(() => {
    logoutRef.current = logout
  })

  return (
    <UserLoginContext.Provider
      value={{
        user,
        isPending,
        firstLogin,
        onCodeResponse,
        onAuthSuccess,
        updateSessionToken,
        logout,
      }}>
      {children}
    </UserLoginContext.Provider>
  )
}
